import React, { useEffect, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, MessageSquareText, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useCreateTalentDiagnosticsQuestion,
  useDeleteTalentDiagnosticsQuestion,
  useTalentDiagnosticsQuestions,
  useTalentDiagnosticsSections,
  useUpdateTalentDiagnosticsQuestion,
} from '../../../hooks/useTalentDiagnostics';
import {
  buildQuestionPayload,
  DEFAULT_QUESTION_TYPE,
  getDefaultQuestionOptions,
  isOptionEditorVisible,
  QUESTION_TYPES,
  getQuestionOptionCount,
  validateQuestionForm,
  type TalentDiagnosticsQuestionFormValues,
  type TalentDiagnosticsQuestionOption,
} from '../../../utils/talentDiagnosticsQuestions';

interface Props {
  definitionId: string;
}

const createInitialFormState = (): TalentDiagnosticsQuestionFormValues => ({
  key: '',
  text: '',
  description: '',
  type: DEFAULT_QUESTION_TYPE,
  weighting: '0',
  isRequired: true,
  options: getDefaultQuestionOptions(DEFAULT_QUESTION_TYPE),
});

export const TalentDiagnosticsQuestionsTab: React.FC<Props> = ({ definitionId }) => {
  const { data: sections = [] } = useTalentDiagnosticsSections(definitionId);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const { data: questions = [], isLoading } = useTalentDiagnosticsQuestions(selectedSectionId ?? '');
  const createMutation = useCreateTalentDiagnosticsQuestion(selectedSectionId ?? '');
  const updateMutation = useUpdateTalentDiagnosticsQuestion(selectedSectionId ?? '');
  const deleteMutation = useDeleteTalentDiagnosticsQuestion(selectedSectionId ?? '');

  const [form, setForm] = useState<TalentDiagnosticsQuestionFormValues>(createInitialFormState);
  const [editingId, setEditingId] = useState<string | null>(null);
  const optionTextRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (sections.length > 0 && !selectedSectionId) {
      setSelectedSectionId(sections[0].id ?? null);
    }
  }, [sections, selectedSectionId]);

  const resetForm = () => {
    setForm(createInitialFormState());
    setEditingId(null);
  };

  const updateForm = (changes: Partial<TalentDiagnosticsQuestionFormValues>) => {
    setForm((current) => ({ ...current, ...changes }));
  };

  const handleTypeChange = (type: string) => {
    setForm((current) => {
      if (isOptionEditorVisible(type)) {
        return {
          ...current,
          type,
          options: type === 'Rating' ? getDefaultQuestionOptions(type) : current.options.length > 0 ? current.options : getDefaultQuestionOptions(type),
        };
      }

      return { ...current, type, options: [] };
    });
  };

  const updateOption = (index: number, field: 'key' | 'text' | 'score', value: string) => {
    setForm((current) => {
      const options = [...current.options];
      options[index] = {
        ...options[index],
        [field]: field === 'score' ? (value === '' ? '' : Number(value)) : value,
      };
      return { ...current, options };
    });
  };

  const addOption = () => {
    setForm((current) => ({ ...current, options: [...current.options, { key: '', text: '', score: '' }] }));
  };

  const removeOption = (index: number) => {
    setForm((current) => ({
      ...current,
      options: current.options.filter((_, optionIndex) => optionIndex !== index),
    }));
  };

  const focusOptionText = (index: number) => {
    optionTextRefs.current[index]?.focus();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedSectionId) {
      toast.error('Select a section before creating a question');
      return;
    }

    const validation = validateQuestionForm(form);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    const payload = buildQuestionPayload({
      id: editingId ?? undefined,
      sectionId: selectedSectionId,
      key: form.key,
      text: form.text,
      description: form.description,
      type: form.type,
      isRequired: form.isRequired,
      weighting: Number(form.weighting || 0),
      options: form.options,
    });

    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }

    resetForm();
  };

  const startEdit = (question: {
    id?: string;
    key?: string;
    text?: string;
    description?: string;
    type?: string;
    isRequired?: boolean;
    weighting?: number;
    options?: Array<{ key?: string; text?: string; score?: number }>;
    optionsJson?: string | null;
  }) => {
    const questionType = question.type ?? DEFAULT_QUESTION_TYPE;
    const parsedOptions: TalentDiagnosticsQuestionOption[] = Array.isArray(question.options)
      ? question.options.map((option) => ({
          key: option.key ?? '',
          text: option.text ?? '',
          score: typeof option.score === 'number' ? option.score : '',
        }))
      : [];
    const serializedOptions: TalentDiagnosticsQuestionOption[] = typeof question.optionsJson === 'string' && question.optionsJson.trim()
      ? (() => {
          try {
            const parsed = JSON.parse(question.optionsJson);
            return Array.isArray(parsed)
              ? parsed.map((option: { key?: string; text?: string; score?: number | null }) => ({
                  key: option.key ?? '',
                  text: option.text ?? '',
                  score: typeof option.score === 'number' ? option.score : '',
                }))
              : [];
          } catch {
            return [];
          }
        })()
      : [];

    const nextOptions = isOptionEditorVisible(questionType)
      ? (parsedOptions.length > 0 ? parsedOptions : serializedOptions.length > 0 ? serializedOptions : getDefaultQuestionOptions(questionType))
      : [];

    setEditingId(question.id ?? null);
    setForm({
      key: question.key ?? '',
      text: question.text ?? '',
      description: question.description ?? '',
      type: questionType,
      weighting: String(question.weighting ?? 0),
      isRequired: question.isRequired ?? true,
      options: nextOptions,
    });
  };

  const handleDelete = (id?: string) => {
    if (!id) return;
    if (window.confirm('Delete this question?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5 text-left">
        <div className="flex items-center gap-2">
          <MessageSquareText size={18} className="text-strivre-burgundy" />
          <h4 className="text-base font-semibold text-slate-900">Create or edit questions</h4>
        </div>
        <p className="mt-1 text-sm text-slate-500">Assign questions to the selected section and shape the response input.</p>

        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Section</label>
            <select
              value={selectedSectionId ?? ''}
              onChange={(event) => setSelectedSectionId(event.target.value || null)}
              className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
            >
              {sections.length === 0 ? (
                <option value="">No sections available</option>
              ) : (
                sections.map((section) => (
                  <option key={section.id} value={section.id ?? ''}>
                    {section.name || 'Untitled section'}
                  </option>
                ))
              )}
            </select>
          </div>

          {!selectedSectionId ? (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <p>Create or select a section first to manage questions for this definition.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Question key</label>
                  <input
                    value={form.key}
                    onChange={(event) => updateForm({ key: event.target.value })}
                    className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    placeholder="e.g. teamCulture"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Weighting</label>
                  <input
                    type="number"
                    value={form.weighting}
                    onChange={(event) => updateForm({ weighting: event.target.value })}
                    className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Question text</label>
                  <input
                    value={form.text}
                    onChange={(event) => updateForm({ text: event.target.value })}
                    className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    placeholder="e.g. How would you rate your team culture?"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Input type</label>
                  <select
                    value={form.type}
                    onChange={(event) => handleTypeChange(event.target.value)}
                    className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  >
                    {QUESTION_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Description</label>
                <input
                  value={form.description}
                  onChange={(event) => updateForm({ description: event.target.value })}
                  className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  placeholder="Optional guidance"
                />
              </div>

              {isOptionEditorVisible(form.type) ? (
                <div className="rounded-2xl border border-strivre-lightnavy bg-slate-50/70 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h5 className="text-sm font-semibold text-slate-900">Question options</h5>
                      <p className="text-sm text-slate-500">Define the selectable values and their score for this question.</p>
                    </div>
                    <button
                      type="button"
                      onClick={addOption}
                      className="rounded-xl border border-strivre-lightnavy bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-strivre-softgrey"
                    >
                      Add option
                    </button>
                  </div>

                  <div className="mt-4 overflow-hidden rounded-xl border border-strivre-lightnavy bg-white">
                    <div className="grid gap-3 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid-cols-[1fr,1.8fr,1fr,auto]">
                      <span>Key</span>
                      <span>Display text</span>
                      <span>Score</span>
                      <span>Actions</span>
                    </div>
                    {form.options.map((option, index) => (
                      <div key={`${option.key}-${index}`} className="grid gap-3 border-t border-strivre-lightnavy px-4 py-3 md:grid-cols-[1fr,1.8fr,1fr,auto]">
                        <input
                          value={option.key}
                          onChange={(event) => updateOption(index, 'key', event.target.value)}
                          className="w-full rounded-lg border border-strivre-lightnavy px-3 py-2 text-slate-900 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                          placeholder="1"
                        />
                        <input
                          ref={(element) => {
                            optionTextRefs.current[index] = element;
                          }}
                          value={option.text}
                          onChange={(event) => updateOption(index, 'text', event.target.value)}
                          className="w-full rounded-lg border border-strivre-lightnavy px-3 py-2 text-slate-900 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                          placeholder="Not in place"
                        />
                        <input
                          type="number"
                          value={option.score === null ? '' : option.score}
                          onChange={(event) => updateOption(index, 'score', event.target.value)}
                          className="w-full rounded-lg border border-strivre-lightnavy px-3 py-2 text-slate-900 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                          placeholder="20"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => focusOptionText(index)}
                            className="rounded-lg border border-strivre-lightnavy px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-strivre-softgrey"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeOption(index)}
                            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={form.isRequired}
                  onChange={(event) => updateForm({ isRequired: event.target.checked })}
                />
                Required
              </label>
              <div className="flex items-center gap-3">
                <button type="submit" className="flex items-center gap-2 rounded-xl bg-strivre-burgundy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-900">
                  <Plus size={16} />
                  {editingId ? 'Save question' : 'Create question'}
                </button>
                {editingId ? (
                  <button type="button" onClick={resetForm} className="rounded-xl border border-strivre-lightnavy px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
                    Cancel
                  </button>
                ) : null}
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5 text-left">
        <h4 className="text-base font-semibold text-slate-900">Questions in this section</h4>
        <p className="text-sm text-slate-500">These are the questions submitted to the selected section.</p>

        {isLoading ? (
          <div className="mt-4 rounded-2xl border border-dashed border-strivre-lightnavy bg-slate-50/70 p-8 text-center text-sm text-slate-500">
            Loading questions...
          </div>
        ) : questions.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-strivre-lightnavy bg-slate-50/70 p-8 text-center text-sm text-slate-500">
            No questions found for this section yet.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {questions.map((question) => (
              <div key={question.id ?? `${question.text}-${question.description}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-strivre-lightnavy bg-slate-50/60 p-4">
                <div>
                  <p className="font-semibold text-slate-900">{question.text || 'Untitled question'}</p>
                  <p className="text-sm text-slate-500">
                    {question.description || 'No description provided'} • Type: {question.type || 'Text'} • Weighting: {question.weighting ?? 0} • Options: {getQuestionOptionCount(question)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => startEdit(question)} className="rounded-xl border border-strivre-lightnavy bg-white p-2 text-slate-600 transition hover:bg-strivre-softgrey">
                    <Pencil size={16} />
                  </button>
                  <button type="button" onClick={() => handleDelete(question.id)} className="rounded-xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
